import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../lib/api';

export default function ProfileDropdown({ user, onNavigate, onLogout, showInstall, onInstallClick }) {
  const [open, setOpen] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);
  const cachedRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleToggle = useCallback(() => {
    const willOpen = !open;
    setOpen(willOpen);
    if (willOpen && !cachedRef.current) {
      setLoading(true);
      api.getCustomer()
        .then((data) => {
          cachedRef.current = data;
          setCustomer(data);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    } else if (willOpen && cachedRef.current) {
      setCustomer(cachedRef.current);
    }
  }, [open]);

  const formatAddress = (addr) => {
    if (!addr) return 'Not available';
    const line1 = addr.line1 || addr.address1 || '';
    const line2 = addr.line2 || addr.address2 || '';
    const city = addr.city || '';
    const state = addr.state || addr.stateProvince || '';
    const zip = addr.zip || addr.postalCode || '';
    return (
      <span>
        {line1 && <>{line1}<br /></>}
        {line2 && <>{line2}<br /></>}
        {city}{city && (state || zip) ? ', ' : ''}{state} {zip}
      </span>
    );
  };

  const customerName = user?.customerName || 'Account';
  // DynamoDB-backed portal users (employees + customer admins) have a
  // `role` on the JWT. Legacy company-password logins don't.
  const isPortalUser = !!user?.role;
  const displayName = user?.name || user?.email || customerName;
  // Pretty role label — keep short for the dropdown.
  const roleLabel = user?.role === 'customer_admin' ? 'Admin'
    : user?.role === 'customer_user' ? 'Employee'
    : null;

  // Pull the SOS customer's primary contact (whoever the company is
  // registered under) for the Company section.
  const primaryContact = customer?.contact
    ? (typeof customer.contact === 'object'
      ? [customer.contact.title, customer.contact.firstName, customer.contact.middleName, customer.contact.lastName, customer.contact.suffix].filter(Boolean).join(' ')
      : customer.contact)
    : null;

  return (
    <div ref={containerRef} style={styles.container}>
      <button style={styles.trigger} onClick={handleToggle}>
        {displayName}
        <span style={styles.caret}>{open ? '\u25B2' : '\u25BC'}</span>
      </button>

      {/* Mobile-only backdrop. Tapping it closes the sheet. Only renders
          when open AND only visible at <=768px via CSS. */}
      {open && <div data-profile-backdrop onClick={() => setOpen(false)} style={styles.backdrop} />}

      {open && (
        <div data-profile-sheet style={styles.dropdown}>
          {/* Mobile drag-handle indicator at the top of the sheet — visual
              cue that this is a bottom sheet. Hidden on desktop via CSS. */}
          <div data-sheet-handle style={styles.sheetHandle} />
          {/* Your Account — only shown for DynamoDB-backed users (they have
              their own email/role distinct from the company's primary
              contact). Legacy customers skip this section. */}
          {isPortalUser && (
            <div style={styles.section}>
              <div style={styles.sectionTitle}>Your Account</div>
              <div style={styles.detailGrid}>
                {user?.name && (
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>Name</span>
                    <span style={styles.detailValue}>{user.name}</span>
                  </div>
                )}
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Email</span>
                  <span style={styles.detailValue}>{user?.email || '-'}</span>
                </div>
                {roleLabel && (
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>Role</span>
                    <span style={styles.detailValue}>{roleLabel}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Company — the SOS customer record. Primary contact is whoever
              is registered with Silver Service; may differ from the
              signed-in user. */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>Company</div>
            {loading ? (
              <div style={styles.muted}>Loading...</div>
            ) : customer ? (
              <div style={styles.detailGrid}>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Name</span>
                  <span style={styles.detailValue}>{customer.name || '-'}</span>
                </div>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Primary Contact</span>
                  <span style={styles.detailValue}>{primaryContact || '-'}</span>
                </div>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Phone</span>
                  <span style={styles.detailValue}>{customer.phone || '-'}</span>
                </div>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Email</span>
                  <span style={styles.detailValue}>{customer.email || '-'}</span>
                </div>
              </div>
            ) : (
              <div style={styles.muted}>Unable to load details</div>
            )}
          </div>

          {/* Address book */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>Address Book</div>
            <div style={styles.addressBlock}>
              <div style={styles.addressLabel}>Delivery Address</div>
              <div style={styles.addressValue}>
                {loading ? 'Loading...' : customer ? formatAddress(customer.shipping) : '-'}
              </div>
            </div>
            <div style={{ ...styles.addressBlock, marginTop: '0.6rem' }}>
              <div style={styles.addressLabel}>Billing Address</div>
              <div style={styles.addressValue}>
                {loading ? 'Loading...' : customer ? formatAddress(customer.billing) : '-'}
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>Quick Links</div>
            {showInstall && (
              <button
                style={styles.linkBtn}
                onClick={() => { onInstallClick && onInstallClick(); setOpen(false); }}
              >
                📲 Install on phone
              </button>
            )}
            <button
              style={styles.linkBtn}
              onClick={() => { onNavigate('addresses'); setOpen(false); }}
            >
              Add/Edit Address
            </button>
            <button
              style={styles.linkBtn}
              onClick={() => { onNavigate('team'); setOpen(false); }}
            >
              Manage Team
            </button>
            <a
              style={styles.linkBtn}
              href={`mailto:orders@silverservice.com?subject=Update Billing Contact - ${customerName}`}
            >
              Update Billing Contact
            </a>
            {user?.features?.invoices && (
              <button
                style={styles.linkBtn}
                onClick={() => { onNavigate('invoices'); setOpen(false); }}
              >
                View Invoices
              </button>
            )}
            <a
              style={styles.linkBtn}
              href="/help"
              target="_blank"
              rel="noopener"
              onClick={() => setOpen(false)}
            >
              Help &amp; Quick Start
            </a>
          </div>

          {/* Sign Out */}
          <div style={styles.signOutSection}>
            <button style={styles.signOutBtn} onClick={onLogout}>
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    position: 'relative',
  },
  trigger: {
    background: '#fff',
    border: '1px solid #e0e0e0',
    color: '#333',
    fontSize: '0.95rem',
    fontWeight: 500,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.55rem 0.95rem',
    borderRadius: 8,
    fontFamily: 'inherit',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
  },
  caret: {
    fontSize: '0.7rem',
    color: 'rgba(0,0,0,0.5)',
    marginLeft: 2,
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: '0.5rem',
    width: 320,
    maxWidth: '90vw',
    // Cap height to the viewport minus the header (~60px) plus a little
    // breathing room — keeps the bottom links (Sign Out, etc.) reachable
    // on short windows by letting the dropdown scroll internally instead
    // of getting clipped under the bottom of the screen.
    maxHeight: 'calc(100vh - 80px)',
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: 10,
    boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
    zIndex: 200,
    overflowY: 'auto',
    overflowX: 'hidden',
    // Keep momentum scrolling on touchpads + iPads.
    overscrollBehavior: 'contain',
  },
  // Backdrop only visible on mobile (controlled in index.html @media block).
  // On desktop the dropdown floats and clicks elsewhere close it via the
  // outside-click effect, so no backdrop is needed.
  backdrop: {
    display: 'none',
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.4)',
    zIndex: 199,
  },
  // Drag handle at the top of the sheet on mobile only — visual affordance
  // saying "you can swipe this down to close." Hidden on desktop.
  sheetHandle: {
    display: 'none',
    width: 40,
    height: 4,
    borderRadius: 4,
    background: '#d4d4d4',
    margin: '0.5rem auto 0',
  },
  section: {
    padding: '0.9rem 1rem',
    borderBottom: '1px solid #f0f0f0',
  },
  sectionTitle: {
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: 'rgba(0,0,0,0.4)',
    marginBottom: '0.5rem',
  },
  detailGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem',
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: '0.5rem',
  },
  detailLabel: {
    fontSize: '0.8rem',
    color: 'rgba(0,0,0,0.4)',
    flexShrink: 0,
  },
  detailValue: {
    fontSize: '0.8rem',
    color: '#333',
    textAlign: 'right',
    wordBreak: 'break-word',
  },
  muted: {
    fontSize: '0.8rem',
    color: 'rgba(0,0,0,0.4)',
  },
  addressBlock: {},
  addressLabel: {
    fontSize: '0.75rem',
    color: 'rgba(0,0,0,0.4)',
    marginBottom: '0.15rem',
  },
  addressValue: {
    fontSize: '0.8rem',
    color: '#333',
    lineHeight: 1.5,
  },
  linkBtn: {
    display: 'block',
    width: '100%',
    background: 'none',
    border: 'none',
    padding: '0.4rem 0',
    color: '#A50B18',
    fontSize: '0.85rem',
    cursor: 'pointer',
    textAlign: 'left',
    textDecoration: 'none',
    fontFamily: 'inherit',
  },
  signOutSection: {
    padding: '0.7rem 1rem',
  },
  signOutBtn: {
    width: '100%',
    background: 'none',
    border: '1px solid #ddd',
    color: 'rgba(0,0,0,0.5)',
    padding: '0.45rem 0',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: '0.8rem',
    fontFamily: 'inherit',
    transition: 'all 0.15s',
  },
};
